import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import Groups2RoundedIcon from "@mui/icons-material/Groups2Rounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AdminHeader from "../../AdminHeader";
import { useAuth } from "../../../auth/useAuth";
import OrganizationUsersTab from "../organisation/OrganizationUsersTab";
import OrganizationRolesTab from "../organisation/OrganizationRolesTab";
import { can } from "../../../auth/can";
import OrganizationAuditLogsTab from "../organisation/OrganizationAuditLogsTab";
import OrganizationNotificationSettings from "../OrganizationNotificationSettings";
import OrganizationLicensingTab from "../organisation/OrganizationLicensingTab";

import { APP_API_V1_BASE } from "../../../config/api";
const API_URL = `${APP_API_V1_BASE}/organizations`;
const PANEL_BG = "#ffffff";
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#667085";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusTone = (status) =>
  status === "active"
    ? { color: "#166534", bg: "#dcfce7", border: "#86efac", label: "Active" }
    : { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: "Disabled" };

const typeTone = (type) => {
  const value = String(type || "").toUpperCase();
  const map = {
    OEM: { color: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
    DISTRIBUTOR: { color: "#9a3412", bg: "#ffedd5", border: "#fdba74" },
    CUSTOMER: { color: "#7c2d12", bg: "#ffedd5", border: "#fed7aa" },
    ENTERPRISE: { color: "#6d28d9", bg: "#f3e8ff", border: "#d8b4fe" },
  };
  return map[value] || { color: "#334155", bg: "#f8fafc", border: "#cbd5e1" };
};

// ─── SummaryCard ─────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, tone = "default" }) => {
  const tones = {
    default: {
      bg: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
      border: "rgba(148,163,184,0.2)",
      iconBg: "linear-gradient(135deg, #e2e8f0, #cbd5e1)",
      iconColor: "#475569",
      iconBorder: "rgba(148,163,184,0.3)",
      labelColor: "#64748B",
      valueColor: "#0F172A",
      glow: "rgba(148,163,184,0.0)",
      accentBar: "#94A3B8",
    },
    blue: {
      bg: "linear-gradient(135deg, #f0f4ff, #e8f0fe)",
      border: "rgba(99,102,241,0.2)",
      iconBg: "linear-gradient(135deg, #c7d2fe, #a5b4fc)",
      iconColor: "#3730a3",
      iconBorder: "rgba(99,102,241,0.25)",
      labelColor: "#4338ca",
      valueColor: "#1e1b4b",
      glow: "rgba(99,102,241,0.06)",
      accentBar: "#6366f1",
    },
    green: {
      bg: "linear-gradient(135deg, #f0fdf6, #dcfce7)",
      border: "rgba(16,185,129,0.2)",
      iconBg: "linear-gradient(135deg, #a7f3d0, #6ee7b7)",
      iconColor: "#065f46",
      iconBorder: "rgba(16,185,129,0.25)",
      labelColor: "#059669",
      valueColor: "#022c22",
      glow: "rgba(16,185,129,0.06)",
      accentBar: "#10b981",
    },
    amber: {
      bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
      border: "rgba(245,158,11,0.2)",
      iconBg: "linear-gradient(135deg, #fde68a, #fcd34d)",
      iconColor: "#92400e",
      iconBorder: "rgba(245,158,11,0.25)",
      labelColor: "#d97706",
      valueColor: "#451a03",
      glow: "rgba(245,158,11,0.06)",
      accentBar: "#f59e0b",
    },
  };
  const t = tones[tone] || tones.default;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${t.border}`,
        background: t.bg,
        boxShadow: `0 4px 24px ${t.glow}, 0 1px 3px rgba(15,23,42,0.06)`,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 12px 32px ${t.glow}, 0 4px 12px rgba(15,23,42,0.08)`,
        },
      }}
    >
      {/* Accent bar along the top */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: t.accentBar,
          opacity: 0.7,
        }}
      />

      <CardContent sx={{ p: 2.5, pt: 3, "&:last-child": { pb: 2.5 } }}>
        <Stack spacing={1.75}>
          {/* Icon + label row */}
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: t.iconBg,
                border: `1px solid ${t.iconBorder}`,
                color: t.iconColor,
                "& svg": { fontSize: 20 },
              }}
            >
              {icon}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: t.labelColor,
              }}
            >
              {label}
            </Typography>
          </Stack>

          {/* Value */}
          <Typography
            sx={{
              fontSize: { xs: 20, md: 22 },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: t.valueColor,
              lineHeight: 1.1,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const InfoRow = ({ label, value, emphasize = false }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1.35,
    }}
  >
    <Typography variant="body2" sx={{ color: TEXT_MUTED, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: TEXT_PRIMARY,
        fontWeight: emphasize ? 800 : 600,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </Typography>
  </Box>
);

const loadingCards = [1, 2, 3, 4];
const loadingRows = [1, 2, 3, 4, 5, 6];
const loadingTabs = [1, 2, 3, 4, 5];
const loadingTableRows = [1, 2, 3, 4, 5];

const OrganizationDetailsSkeleton = () => (
  <Box
    sx={{
      margin: "0 auto",
      p: { xs: 2, md: 3 },
      background:
        "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
    }}
  >
    <AdminHeader title="Organization Details" />

    <Stack spacing={3}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          boxShadow: "0 16px 48px rgba(15,23,42,0.06)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={2}>
            <Skeleton
              variant="rounded"
              width={170}
              height={38}
              sx={{ borderRadius: 3, bgcolor: "rgba(255,255,255,0.18)" }}
            />
            <Skeleton
              variant="text"
              width="min(420px, 80%)"
              height={42}
              sx={{ bgcolor: "rgba(255,255,255,0.18)" }}
            />
            <Skeleton
              variant="text"
              width="min(320px, 70%)"
              height={24}
              sx={{ bgcolor: "rgba(255,255,255,0.14)" }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Skeleton
                variant="rounded"
                width={88}
                height={32}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.16)" }}
              />
              <Skeleton
                variant="rounded"
                width={118}
                height={32}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.16)" }}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            xl: "repeat(4, 1fr)",
          },
          gap: 2,
        }}
      >
        {loadingCards.map((item) => (
          <Card
            key={item}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid rgba(148,163,184,0.18)",
              background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
              boxShadow: "0 4px 24px rgba(15,23,42,0.04)",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={1.75}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: 2 }} />
                  <Skeleton variant="text" width="45%" height={20} />
                </Stack>
                <Skeleton variant="text" width="70%" height={34} />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(17, 24, 39, 0.08)",
          boxShadow: "0 16px 40px rgba(15,23,42,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.25, md: 3 },
            py: 1.25,
            borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
            backgroundColor: "#fbfcfd",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ overflow: "hidden" }}>
            {loadingTabs.map((item) => (
              <Skeleton
                key={item}
                variant="rounded"
                width={item === 3 ? 150 : 96}
                height={40}
                sx={{ borderRadius: 999, flexShrink: 0 }}
              />
            ))}
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: "1px solid rgba(17, 24, 39, 0.08)",
              boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={240} height={32} />
              <Skeleton variant="text" width="min(520px, 85%)" height={24} sx={{ mb: 1.5 }} />
              <Stack spacing={1.35}>
                {loadingRows.map((item) => (
                  <Stack
                    key={item}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Skeleton variant="text" width="28%" height={24} />
                    <Skeleton variant="text" width={item % 2 ? "36%" : "22%"} height={24} />
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Stack>
  </Box>
);

const OverviewTabSkeleton = () => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    {[1, 2].map((card) => (
      <Card
        key={card}
        elevation={0}
        sx={{
          borderRadius: 3.5,
          border: "1px solid rgba(17, 24, 39, 0.08)",
          boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={card === 1 ? 230 : 210} height={32} />
          <Skeleton
            variant="text"
            width="min(520px, 85%)"
            height={24}
            sx={{ mb: 1.5 }}
          />
          <Stack spacing={1.35}>
            {loadingRows.slice(0, card === 1 ? 6 : 3).map((item) => (
              <Stack
                key={item}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Skeleton variant="text" width="28%" height={24} />
                <Skeleton
                  variant="text"
                  width={item % 2 ? "36%" : "22%"}
                  height={24}
                />
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    ))}
  </Box>
);

const TabContentSkeleton = ({ activeTab }) => {
  if (activeTab === "overview") {
    return <OverviewTabSkeleton />;
  }

  const showSummaryCards = activeTab === "billing" || activeTab === "notifications";

  return (
    <Stack spacing={2.25}>
      {showSummaryCards && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {[1, 2, 3].map((item) => (
            <Card
              key={item}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(17, 24, 39, 0.08)",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Skeleton variant="text" width="50%" height={22} />
                <Skeleton variant="text" width="72%" height={34} />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 3.5,
          border: "1px solid rgba(17, 24, 39, 0.08)",
          boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2.25}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Box>
                <Skeleton variant="text" width={220} height={32} />
                <Skeleton variant="text" width={280} height={22} />
              </Box>
              <Skeleton
                variant="rounded"
                width={130}
                height={38}
                sx={{ borderRadius: 2 }}
              />
            </Stack>

            <Stack spacing={1.25}>
              {loadingTableRows.map((item) => (
                <Box
                  key={item}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1.4fr 1fr 1fr 120px",
                    },
                    gap: 1.5,
                    alignItems: "center",
                    py: 1.25,
                    borderBottom:
                      item === loadingTableRows.length
                        ? "none"
                        : "1px solid rgba(17, 24, 39, 0.06)",
                  }}
                >
                  <Skeleton variant="text" width="80%" height={26} />
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="65%" height={24} />
                  <Skeleton
                    variant="rounded"
                    width={92}
                    height={28}
                    sx={{ borderRadius: 999 }}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default function OrganizationDetails() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrg, permissions } = useAuth();
  const [org, setOrg] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tabContentLoading, setTabContentLoading] = useState(false);
  const [error, setError] = useState("");
  const previousTabRef = useRef(null);

  const tabConfig = [
    { key: "overview", label: "Overview", permission: "organization.view" },
    { key: "users", label: "Users", permission: "organization.user.view" },
    {
      key: "roles",
      label: "Roles & Permissions",
      permission: "organization.role.view",
    },
    {
      key: "audit",
      label: "Audit Logs",
      permission: "organization.audit.view",
      altPermission: "audit.view",
    },
    {
      key: "notifications",
      label: "Notifications",
      permission: "notification.view",
    },
    {
      key: "billing",
      label: "Billing & Licensing",
      permission: "organization.view",
    },
  ];

  const visibleTabs = useMemo(
    () =>
      tabConfig.filter((item) => {
        const isPlatformOwnerOrg =
          String(org?.type || "").toUpperCase() === "OEM";
        if (item.key === "billing" && isPlatformOwnerOrg) {
          return false;
        }

        return (
          can(permissions, item.permission) ||
          (item.altPermission && can(permissions, item.altPermission))
        );
      }),
    [org?.type, permissions],
  );

  const activeTab = visibleTabs[tab]?.key || visibleTabs[0]?.key || "overview";

  useEffect(() => {
    if (loading) return undefined;

    if (!previousTabRef.current) {
      previousTabRef.current = activeTab;
      return undefined;
    }

    if (previousTabRef.current === activeTab) {
      return undefined;
    }

    previousTabRef.current = activeTab;
    setTabContentLoading(true);

    const timer = setTimeout(() => {
      setTabContentLoading(false);
    }, 450);

    return () => clearTimeout(timer);
  }, [activeTab, loading]);

  useEffect(() => {
    if (!visibleTabs.length) return;

    const queryTab = new URLSearchParams(location.search).get("tab");
    if (!queryTab) return;

    const nextIndex = visibleTabs.findIndex((item) => item.key === queryTab);
    if (nextIndex >= 0 && nextIndex !== tab) {
      setTab(nextIndex);
    }
  }, [location.search, tab, visibleTabs]);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        setLoading(true);
        setError("");
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`${API_URL}/${orgId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id ? { "X-Org-ID": activeOrg.id } : {}),
          },
        });
        setOrg(res.data?.data || res.data);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load organization");
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [orgId, activeOrg]);

  useEffect(() => {
    if (tab >= visibleTabs.length) {
      setTab(0);
    }
  }, [visibleTabs, tab]);

  if (loading) {
    return <OrganizationDetailsSkeleton />;
  }

  if (error || !org) {
    return (
      <Stack
        sx={{ minHeight: "60vh" }}
        alignItems="center"
        justifyContent="center"
        spacing={2}
      >
        <Alert severity="error" sx={{ borderRadius: 3, maxWidth: 560 }}>
          {error || "Organization not found"}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate("/admin/organizations")}
          sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
        >
          Back to Organizations
        </Button>
      </Stack>
    );
  }

  const status = statusTone(org.status);
  const type = typeTone(org.type);

  const handleTabChange = (_, value) => {
    setTab(value);

    const nextKey = visibleTabs[value]?.key;
    if (!nextKey) return;

    const params = new URLSearchParams(location.search);
    params.set("tab", nextKey);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <Box
      sx={{
        margin: "0 auto",
        p: { xs: 2, md: 3 },
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
      }}
    >
      <AdminHeader
        title="Organization Details"
        breadcrumbs={[
          { label: "Administration", path: "/administration" },
          { label: "Organizations", path: "/admin/organizations" },
          { label: org.name },
        ]}
      />

      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          borderRadius: 3,
          overflow: "hidden",
          mb: 3,
          boxShadow: "0 16px 48px rgba(15,23,42,0.06)",
        }}
      >
        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", xl: "row" },
            justifyContent: "space-between",
            alignItems: { xl: "center" },
            gap: 3,
          }}
        >
          <Box display="flex" gap={2} alignItems="flex-start">
            {/* ICON */}
            {/* <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: 4,
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "center",
          color: "#312e81",
          background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
          border: "1px solid rgba(99, 102, 241, 0.16)",
        }}
      >
        <ApartmentRoundedIcon sx={{ fontSize: 34 }} />
      </Box> */}

            {/* TEXT */}
            <Stack spacing={0.8}>
              <Box
  sx={{
    display: "flex",
    flexDirection: "column",
    gap: 1.5, // ✅ THIS creates vertical space
  }}
>
  <Button
    variant="outlined"
    startIcon={<ArrowBackRoundedIcon />}
    onClick={() => navigate("/admin/organizations")}
    sx={{
      borderRadius: 3,
      textTransform: "none",
      fontWeight: 600,
      color: "#ffffff",
      borderColor: "rgba(255,255,255,0.4)",
      "&:hover": {
        borderColor: "#fff",
        backgroundColor: "rgba(255,255,255,0.08)",
      },
    }}
  >
    Back to Organizations
  </Button>

  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      color: "#ffffff",
      lineHeight: 1.2,
    }}
  >
    {org.name}
  </Typography>
</Box>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                }}
              >
                Tenant code <b>{org.code || "-"}</b> •{" "}
                {String(org.type || "Unknown").toUpperCase()}
              </Typography>

              {/* CHIPS */}
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={status.label}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                />

                <Chip
                  label={String(org.type || "Unknown").toUpperCase()}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                />
              </Stack>
            </Stack>
          </Box>

          {/* BUTTON RIGHT SIDE */}
          <Stack
            direction="row"
            justifyContent={{ xs: "flex-start", xl: "flex-end" }}
            width={{ xs: "100%", xl: "auto" }}
          >
            
          </Stack>
        </Box>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            xl: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryCard
          icon={<BadgeRoundedIcon />}
          label="Organization Code"
          value={org.code || "—"}
          tone="blue"
        />
        <SummaryCard
          icon={<VerifiedRoundedIcon />}
          label="Lifecycle State"
          value={status.label}
          tone="green"
        />
        <SummaryCard
          icon={<Inventory2RoundedIcon />}
          label="Tenant Type"
          value={String(org.type || "—").toUpperCase()}
          tone="amber"
        />
        <SummaryCard
          icon={<Groups2RoundedIcon />}
          label="Created"
          value={formatDate(org.created_at)}
        />
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid",
          borderColor: "rgba(17, 24, 39, 0.08)",
          boxShadow: "0 16px 40px rgba(15,23,42,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2.25, md: 3 },
            pt: 1.25,
            borderBottom: "1px solid rgba(17, 24, 39, 0.08)",
            backgroundColor: "#fbfcfd",
          }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                minWidth: "auto",
                px: { xs: 2, md: 2.5 },
                py: 1.5,
                mr: 1,
                borderRadius: 999,
                textTransform: "none",
                fontSize: 15,
                fontWeight: 700,
                color: "#64748B",
              },
              "& .Mui-selected": {
                color: "#0F4C81 !important",
                backgroundColor: "rgba(14,165,233,0.10)",
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 999,
                background: "linear-gradient(90deg, #0EA5E9, #14B8A6)",
              },
            }}
          >
            {visibleTabs.map((item) => (
              <Tab key={item.key} label={item.label} disableRipple />
            ))}
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
          {activeTab === "overview" && (
            <Box
  sx={{
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  }}
>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column", // ✅ always stacked
                  gap: 2,
                }}
              >
                {/* Organization Profile */}
                <Box sx={{ width: "100%" }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3.5,
                      border: "1px solid",
                      borderColor: "rgba(17, 24, 39, 0.08)",
                      boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: "-0.02em",
                          color: TEXT_PRIMARY,
                          mb: 0.5,
                        }}
                      >
                        Organization Profile
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: TEXT_MUTED, mb: 2 }}
                      >
                        High-level tenant identity and current administrative
                        status.
                      </Typography>

                      <InfoRow label="Organization ID" value={org.id} />
                      <InfoRow
                        label="Display Name"
                        value={org.name}
                        emphasize
                      />
                      <InfoRow label="Code" value={org.code} emphasize />
                      <InfoRow
                        label="Type"
                        value={String(org.type || "-").toUpperCase()}
                      />
                      <InfoRow label="Status" value={status.label} />
                      <InfoRow
                        label="Created At"
                        value={formatDate(org.created_at)}
                      />
                    </CardContent>
                  </Card>
                </Box>

                {/* Administrative Notes */}
                <Box sx={{ width: "100%" }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3.5,
                      border: "1px solid",
                      borderColor: "rgba(17, 24, 39, 0.08)",
                      boxShadow: "0 12px 32px rgba(15,23,42,0.04)",
                      height: "100%",
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: "-0.02em",
                          color: TEXT_PRIMARY,
                          mb: 0.5,
                        }}
                      >
                        Administrative Notes
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: TEXT_MUTED, mb: 2 }}
                      >
                        Use the tabs to review users, permissions, audit trail,
                        and notification setup for this organization.
                      </Typography>

                      <Stack spacing={1.25}>
                        <Chip
                          label={`Tenant Type: ${String(org.type || "-").toUpperCase()}`}
                          sx={{
                            justifyContent: "flex-start",
                            borderRadius: 2,
                            fontWeight: 700,
                            color: type.color,
                            backgroundColor: type.bg,
                            border: `1px solid ${type.border}`,
                          }}
                        />
                        <Chip
                          label={`Current Status: ${status.label}`}
                          sx={{
                            justifyContent: "flex-start",
                            borderRadius: 2,
                            fontWeight: 700,
                            color: status.color,
                            backgroundColor: status.bg,
                            border: `1px solid ${status.border}`,
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === "users" && <OrganizationUsersTab orgId={orgId} />}
          {activeTab === "roles" && <OrganizationRolesTab />}
          {activeTab === "audit" && <OrganizationAuditLogsTab />}
          {activeTab === "notifications" && (
            <OrganizationNotificationSettings />
          )}
          {activeTab === "billing" && (
            <OrganizationLicensingTab orgId={orgId} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
